import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { HealthProfessionalUser } from '../../../app/authentication';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/message-service';
import './clinical-notes-viewer-controller.css';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ActionAPI, FieldAPI, FormAPI, InputAPI, ModalProps, ModalService, ToastService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';

import { DeckContext } from '../../commons/Deck/deck-service';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { ClinicalNotesViewerAliases, FragmentNames } from '../../../codegen';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants/lib';
import { foundationContext } from '@philips-internal/oswald-ui-foundation';
import ClinicalNotesService from '../clinical-note-service';
import { OpenConsentModalService } from '../clinical-notes-consent/open-consent-modal-service';
import { YesNoType } from '../../../utils/enum-utils';

@Controller({ component: ClinicalNotesViewerAliases.LAYOUT_CLINICAL_NOTES_VIEWER, fragment: FragmentNames.CLINICAL_NOTES_VIEWER })
class ClinicalNotesViewerController extends BaseController {

  @Inject(ClinicalNotesViewerAliases.FORM_CLINICAL_NOTE_VIEWER)
  private form!: FormAPI;

  @Inject(ClinicalNotesViewerAliases.INPUT_1)
  private inputDateAPI!: InputAPI;

  @Inject(ClinicalNotesViewerAliases.INPUT_0)
  private inputDateReleaseAPI!: InputAPI;

  @Inject(ClinicalNotesViewerAliases.FIELD_1)
  private fieldDateAPI!: FieldAPI;

  @Inject(ClinicalNotesViewerAliases.FIELD_SPECIALTY_OPINION)
  private fieldSpecialtyOpinion!: FieldAPI;

  @Inject(ClinicalNotesViewerAliases.FIELD_MEDICAL_OPINION)
  private fieldMedicalOpinion!: FieldAPI;

  @Inject(ClinicalNotesViewerAliases.INACTIVATE_CLINICAL_NOTE_ACTION)
  private actionInactivateApi!: ActionAPI;

  @Inject(ClinicalNotesViewerAliases.NEW_CLINICAL_NOTE_ACTION)
  private actionNewApi!: ActionAPI;

  @Inject(ClinicalNotesViewerAliases.DELETE_CLINICAL_NOTE_ACTION)
  private readonly actionDeleteApi!: ActionAPI;

  @OnMount({ target: ClinicalNotesViewerAliases.LAYOUT_CLINICAL_NOTES_VIEWER })
  onMount(): void {

    InformationBarUtils.collapsedBar = true;

    const { cdEvolucao, patientId } = this.historyService.getCurrentRouteParams();
    const itens = DeckContext.getParamActivate();
    const clinicalNotes = itens?.find(item => item.cdEvolucao == cdEvolucao);
    const encounterId = clinicalNotes ? clinicalNotes.nrAtendimento : this.historyService.getCurrentRouteParams().encounterId;
    const isPeriodoRetroativo = 'S';
    const allowInfo = InformationBarUtils.allowInfo && ClinicalNotesService.isAllowEdit;
    const ieTranscricao = clinicalNotes?.ieTranscricao as string;

    DeckContext.clearParamActivate();

    this.context.set({
      encounterId: encounterId,
      evolutionId: cdEvolucao,
      patientId: patientId,
    });

    this.form.setLoading(true);

    HttpService.get(Endpoint.GET_NOTAS_CLINICAS_VIEWER, {
      clinicalNoteId: cdEvolucao,
      encounterId,
      patientId,
    })
      .then(response => {
        const res = response.body as Record<string, Value>;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const dtEvolucao = DateFormatterUtil.timeStamp(String(res.dtEvolucao))
        const dtLiberacao = DateFormatterUtil.timeStamp(String(res.dtLiberacao))
        const dsEvolucao =  String(res?.dsEvolucao ?? '').replaceAll(/\n/g, '<br />');
        const record = { ...this.form.getData(), ...res, dsEvolucao, dtEvolucao, dtLiberacao } as Record<string, Value>;
        const showInactivate = res.professionalCode === user.physicalPersonId && Boolean(res.showInactivate) && allowInfo;
        const showDelete = ieTranscricao === YesNoType.YES && allowInfo && !res.dtLiberacao;

        this.fieldSpecialtyOpinion.setVisible(record.dsEspecialidadeParecer != null);
        this.fieldMedicalOpinion.setVisible(record.nmMedicoParecer != null);
        this.inputDateAPI.setValue(DateFormatterUtil.timeStamp(String(record.dtEvolucao)));
        this.fieldDateAPI.setVisible(!!dtLiberacao);
        this.inputDateReleaseAPI.setValue(DateFormatterUtil.timeStamp(String(record.dtLiberacao)));
        this.actionInactivateApi.setEnabled(showInactivate);
        this.actionDeleteApi.setEnabled(showDelete);

        if (record.iePeriodoRetroativo === isPeriodoRetroativo) {
          record.iePeriodoRetroativo = MessageService.translate('label.status-yes')
        } else {
          record.iePeriodoRetroativo = MessageService.translate('label.status-no');
        }

        this.form.setData(record);
      })
      .catch((error) => this.showToast(error?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.form.setLoading(false);
        this.actionNewApi.setEnabled(allowInfo);
      });
  }

  @OnClick({ target: ClinicalNotesViewerAliases.INACTIVATE_CLINICAL_NOTE_ACTION })
  onInactivate(): void {
    const evolutionId = this.context.get('evolutionId');
    const patientId = this.context.get('patientId');
    const encounterId = this.context.get('encounterId');

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'patient-evolution-inactivation',
      id: 'patient-evolution-inactivation',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      encounterId,
      evolutionId,
      patientId,
    }

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ClinicalNotesViewerAliases.NEW_CLINICAL_NOTE_ACTION })
  onNew(): void {
    OpenConsentModalService.openConsentOptionsModal()
  }

  @OnClick({ target: ClinicalNotesViewerAliases.DELETE_CLINICAL_NOTE_ACTION })
  onDelete(): void {
    const evolutionId = this.context.get('evolutionId');
    const encounterId = this.context.get('encounterId');
    const patientId = this.context.get('patientId');
    const cnService = new ClinicalNotesService(this);

    cnService.confirmDelete(patientId, encounterId, parseInt(evolutionId));
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'clinical-notes-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default ClinicalNotesViewerController;
